import cn from 'classnames';
import { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { getMeals } from 'actions/menuSearch';
import useAnalyticsCallback from 'hooks/useAnalyticsCallback';
import useClickOutside from 'hooks/useClickOutside';
import useKeydown from 'hooks/useKeydown';
import useQuery from 'hooks/useQuery';
import useScreenSizeConstants from 'hooks/useScreenSizeConstants';
import useScroll from 'hooks/useScroll';
import { TEMPO } from 'lib/constants';
import * as Types from 'utilities/types';

import MenuSearch from './MenuSearch';
import MenuSearchDropdown from './MenuSearchDropdown';
import MenuSearchSlideout from './MenuSearchSlideout';

const MenuSearchContainer = ({
  loading,
  mealPlanSlug,
  numberOfPages,
  onGetMeals,
  orderableMeals,
  pastMeals,
  planId,
  showUnconvertedLead,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSlideout, setShowSlideout] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [scrollY, setScrollY] = useState(window.scrollY);
  const { isMobileOrTablet } = useScreenSizeConstants();
  const history = useHistory();
  const searchParams = new URLSearchParams();
  const { queryParams } = useQuery();
  const isTempo = mealPlanSlug === TEMPO;
  const dropdownRef = useRef(null);

  const handleSearch = ({ params }) => {
    const { searchTerm, pageNum = 1 } = params;
    if (searchTerm) {
      searchParams.set('searchTerm', searchTerm);
      searchParams.set('page', pageNum);
      searchParams.set('planId', planId);

      history.push({ search: searchParams.toString() });
    }
  };

  const handleSearchWithAnalytics = useAnalyticsCallback(
    handleSearch,
    ({ events, logEvent, callbackArgs }) => {
      const searchString = callbackArgs[0].params.searchTerm;
      if (searchString !== '') {
        logEvent(events.menuSearch.recipeSearchClicked, {
          searchTerm: callbackArgs[0].params.searchTerm,
          mealPlanSlug: mealPlanSlug,
        });
      }
    },
    []
  );

  const handleSearchFocus = () => {
    setIsDropdownOpen(true);
  };

  const handlePageChange = useAnalyticsCallback(
    pageNum => {
      handleSearch({ params: { searchTerm, pageNum } });
    },
    ({ events, logEvent, callbackArgs }) => {
      const pageNum = callbackArgs[0];
      logEvent(events.menuSearch.recipeSearchPageClicked, {
        searchTerm,
        page: pageNum,
      });
    },
    [searchTerm]
  );

  const closeSlideout = () => {
    setSearchTerm('');
    setPage(1);
    setShowSlideout(false);
    history.push('/our-menu');
  };

  const isAboveScrollThreshold = isMobileOrTablet ? scrollY > 178 : scrollY > 152;

  useClickOutside(dropdownRef, () => {
    setIsDropdownOpen(false);
  });

  useKeydown(() => setIsDropdownOpen(false));

  useScroll(() => setIsDropdownOpen(false));

  useEffect(() => {
    const onScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (queryParams) {
      const searchTermParam = queryParams.get('searchTerm');
      if (searchTermParam) setSearchTerm(searchTermParam);
      const pageParam = queryParams.get('page');
      if (pageParam) setPage(parseInt(pageParam));
    }
  }, [queryParams]);

  useEffect(() => {
    if (searchTerm && page) {
      const limit = 12;
      const offset = 12 * page - 12;
      onGetMeals(searchTerm, planId, limit, offset, page);
      setShowSlideout(true);
    }
  }, [searchTerm, page, onGetMeals, planId]);

  useEffect(() => {
    if (page === 1) {
      return history.listen(() => {
        if (
          history.action === 'POP' &&
          history.location.pathname === '/our-menu' &&
          !history.location.search
        ) {
          closeSlideout();
        }
      });
    }
  }, [page, history, closeSlideout]);

  return (
    <>
      <div
        className={cn('siteHeader--fixed flex w-full flex-col justify-center border-solid pr-4', {
          'h-16 bp1:h-24 bp2:h-20': showUnconvertedLead,
          'h-14 bp2:h-16': !showUnconvertedLead,
          'relative bp1:top-32': !isAboveScrollThreshold,
          'top-36': !isAboveScrollThreshold && isTempo,
          'top-24': !isAboveScrollThreshold && !isTempo,
          'border-b': isAboveScrollThreshold,
          'border-cream-110 bg-cream-100 p-2': !isTempo,
          'border-charcoal-50 bg-tempo-gray-40 p-2 pt-0': isTempo,
        })}
      >
        <div ref={dropdownRef} className="relative mx-auto w-full max-w-lg">
          <MenuSearch
            handleSearch={handleSearchWithAnalytics}
            searchTerm={searchTerm}
            fieldClass={cn(
              'text w-full max-w-lg shadow-md rounded-full h-10 bp2:h-12 border border-solid border-charcoal-60 align-middle',
              { tempo: isTempo }
            )}
            handleSearchFocus={handleSearchFocus}
          />
          {isDropdownOpen && !isTempo && (
            <div className="absolute left-0 top-full z-10 mt-4 w-full">
              <MenuSearchDropdown handleSearch={handleSearchWithAnalytics} />
            </div>
          )}
        </div>
      </div>
      <MenuSearchSlideout
        searchTerm={searchTerm}
        showSlideout={showSlideout}
        handleSearch={handleSearchWithAnalytics}
        closeSlideout={closeSlideout}
        orderableMeals={orderableMeals}
        pastMeals={pastMeals}
        page={page}
        numberOfPages={numberOfPages}
        onUpdatePage={handlePageChange}
        loading={loading}
        showUnconvertedLead={showUnconvertedLead}
        mealPlanSlug={mealPlanSlug}
      />
    </>
  );
};

const mapStateToProps = state => ({
  orderableMeals: state.menuSearch.orderableMeals,
  pastMeals: state.menuSearch.pastMeals,
  numberOfPages: state.menuSearch.numberOfPages,
  loading: state.menuSearch.loading,
});

const mapDispatchToProps = {
  onGetMeals: getMeals,
};

MenuSearchContainer.propTypes = {
  orderableMeals: PropTypes.arrayOf(Types.meal.isRequired),
  pastMeals: PropTypes.arrayOf(Types.meal.isRequired),
  numberOfPages: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  onGetMeals: PropTypes.func.isRequired,
  showUnconvertedLead: PropTypes.bool.isRequired,
  planId: PropTypes.string.isRequired,
  mealPlanSlug: PropTypes.string.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(MenuSearchContainer);
